import React, { ContextType, KeyboardEvent, MouseEvent, PureComponent, ReactElement } from 'react';
import { createPortal } from 'react-dom';

import { ModalAPI } from '@philips-emr/tws-components-api';
import { KeyCode, ModalType, Theme } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { DocumentContext } from '@philips-emr/tws-core/lib/component/context';
import { arrayOf, bool, element, eventListener, expression, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { CloseEvent } from '@philips-emr/tws-core/lib/component/types';
import { classNames, enumValues, isEnumValid, isFunction } from '@philips-emr/tws-utils';

import { ModalClose, ModalNav, ModalTitle } from './components';

import './modal.scss';

const acceptedTypes = [
  ComponentType.AGGREGATOR,
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
  ComponentType.DATA_ENTRY,
  ComponentType.FEEDBACK,
  ComponentType.FORM,
  ComponentType.LAYOUT,
  ComponentType.LIST,
  ComponentType.NAVIGATION,
];

export interface ModalProps {
  children: ReactElement | ReactElement[];
  className?: string;
  closeByDocument: boolean;
  closeByEscape: boolean;
  enableNavigation: boolean;
  focusOnOpen: boolean;
  onBeforeClose?: (event: CloseEvent) => void;
  onClose?: (event: CloseEvent) => void;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  theme?: Theme;
  title?: string;
  type: ModalType;
  uuid: string;
}

interface ModalState {
  modalElement?: HTMLDivElement;
}

@AwesomeComponent({ type: ComponentType.LAYOUT, api: ModalAPI })
class Modal extends PureComponent<ModalProps, ModalState> {
  static contextType = DocumentContext;

  context!: ContextType<typeof DocumentContext>;

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    closeByDocument: bool,
    closeByEscape: bool,
    enableNavigation: bool,
    focusOnOpen: bool,
    onBeforeClose: eventListener,
    onClose: eventListener,
    onNextPage: eventListener,
    onPrevPage: eventListener,
    theme: oneOf(enumValues(Theme)),
    title: expression,
    type: oneOf(enumValues(ModalType)),
    uuid: string,
  };

  static defaultProps = {
    closeByDocument: true,
    closeByEscape: true,
    enableNavigation: false,
    focusOnOpen: true,
    theme: Theme.DARK,
    type: ModalType.DEFAULT,
    uuid: '',
  };

  state: ModalState = {};

  componentDidMount(): void {
    const { document } = this.context;

    document?.documentElement.classList.add('u-prevent-scroll');
  }

  componentWillUnmount(): void {
    const { document } = this.context;

    document?.documentElement.classList.remove('u-prevent-scroll');
  }

  getClassName(): string {
    const { className, type } = this.props;

    const modalClasses = {
      'c-modal': true,
      'is-open': true,
      [`${className}`]: Boolean(className),
      [`c-modal--${type}`]: isEnumValid(ModalType, type),
    };

    return classNames(modalClasses);
  }

  getModalElement(): HTMLElement | null {
    return this.state.modalElement ?? null;
  }

  shouldClose(): boolean {
    const { onBeforeClose } = this.props;

    let close = true;

    if (isFunction(onBeforeClose)) {
      const abortFn = (): void => {
        close = false;
      };

      onBeforeClose(new CloseEvent({ abort: abortFn }));
    }

    return close;
  }

  closeModal = (): void => {
    const shouldClose = this.shouldClose();

    if (!shouldClose) {
      return;
    }

    const { onClose } = this.props;

    if (isFunction(onClose)) {
      onClose(new CloseEvent());
    }
  };

  // Has to be private, otherwise typescript will complain about
  // "Public property 'onDocumentClick' of exported class has or is using private name 'MouseEvent'"
  private onDocumentClick = (event: MouseEvent<HTMLDivElement>): void => {
    const { closeByDocument } = this.props;
    const { target } = event;

    if (!(target instanceof HTMLDivElement)) {
      return;
    }

    const { classList } = target;

    if (!closeByDocument) return;
    if (!classList.contains('c-modal')) return;

    this.closeModal();
  };

  onDocumentKeydown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const { closeByEscape } = this.props;

    if (!closeByEscape || event.keyCode !== KeyCode.ESC) return;

    this.closeModal();
  };

  focus = (): void => {
    const { focusOnOpen } = this.props;
    const { modalElement } = this.state;

    focusOnOpen && modalElement && modalElement.focus();
  };

  modalRef = (element: HTMLDivElement): void => {
    this.setState({ modalElement: element }, this.focus);
  };

  renderModalClose(theme?: Theme): ReactElement | null {
    const { onClose } = this.props;

    if (!isFunction(onClose)) {
      return null;
    }

    return <ModalClose onClick={this.closeModal} theme={theme} />;
  }

  renderNav(className: string, onClick?: () => void): ReactElement | null {
    const { enableNavigation, type } = this.props;

    if (type !== ModalType.DEFAULT || !enableNavigation) {
      return null;
    }

    return <ModalNav className={className} onClick={onClick} />;
  }

  renderContent(): ReactElement | null {
    const { children, title = '', uuid, type, onNextPage, onPrevPage } = this.props;
    let { theme } = this.props;

    const Container = ContainerProvider.provide(this);

    if (!Container) {
      return null;
    }

    const { modalElement } = this.state;

    const modalContext = {
      ...this.context,
      scrollElement: modalElement,
    };

    theme = type === ModalType.FULLSCREEN && title ? Theme.DARK : theme;

    return (
      <DocumentContext.Provider value={modalContext}>
        <div
          className={this.getClassName()}
          onClick={this.onDocumentClick}
          onKeyDownCapture={this.onDocumentKeydown}
          ref={this.modalRef}
          tabIndex={0}
        >
          {this.renderNav('c-modal__nav--prev', onPrevPage)}

          <div className="c-modal__wrapper">
            <ModalTitle>{title}</ModalTitle>
            {this.renderModalClose(theme)}

            <Container
              acceptedTypes={acceptedTypes}
              className="c-modal__content"
              uuid={uuid}
            >
              {children}
            </Container>
          </div>

          {this.renderNav('c-modal__nav--next', onNextPage)}
        </div>
      </DocumentContext.Provider>
    );
  }

  render(): ReactElement | null {
    const { document } = this.context;

    if (!document) {
      return null;
    }

    return createPortal(this.renderContent(), document.body);
  }
}

export default Modal;
